'use client';

import { useTranslations } from 'next-intl';
import { useContext, useEffect, useState } from 'react';

import CardListWrapper from '@/components/search/CardListWrapper';
import ListCounter from '@/components/search/ListCounter';
import LocationSearchList from '@/components/search/LocationSearchList';
import LocationSearchMap from '@/components/search/LocationSearchMap';
import SearchNoResults from '@/components/search/SearchNoResults';
import LocationsSearchListLoader from '@/components/search/cards/locations/LocationsSearchListLoader';
import useDayPassSearchFilters from '@/components/search/useDayPassSearchFilters';
import useHighlightedSlug from '@/components/search/useHighlightedSlug';
import useSearchPageParams from '@/components/search/useSearchPageParams';
import { mapBounds } from '@/components/search/utils';
import {
  SearchDayPassesLocationsProps,
  fetchDayPassLocationPricingByIds,
  searchDayPassesLocations,
} from '@/lib/coworking-day-pass/coworking-day-pass-api';
import { CoworkingDayPassPricesDto } from '@/lib/coworking-day-pass/dto/coworking-day-pass-price.dto';
import { SimpleLocationSearchDto } from '@/lib/locations';
import * as gtm from '@/utils/gtm';

import SearchContext from '../../_components/SearchContext';
import { SearchModalType } from '../../_components/SearchPageWrapper';
import useSearchData from '../../_components/useSearchData';
import { createDayPassQueryParams, mapFilterToDto } from '../utils';
import CoworkingProductExplainer from './CoworkingProductExplainer';
import {
  LocationDateRangeFilters,
  LocationDateRangeModal,
} from './LocationDateRangeModal';

export type DayPassesSearchProps = {
  locale: string;
  mapboxAccessToken: string;
};

export default function DayPassesSearch({
  locale,
  mapboxAccessToken,
}: DayPassesSearchProps) {
  const t = useTranslations('search');

  const { highlightedSlug, onMouseEnter, onMouseLeave } = useHighlightedSlug();
  const {
    isFullScreenMap,
    searchModalType,
    onSearchInputItemClick,
    onCloseModal,
    openModal,
  } = useContext(SearchContext);

  const { filter } = useDayPassSearchFilters();

  const hasFilters = !!filter.dateRange;
  const { updateQuery } = useSearchPageParams();

  const [dayPassesPrices, setDayPassesPrices] =
    useState<CoworkingDayPassPricesDto>();

  const {
    isWebGLSupported,
    cardListRef,
    isLoading,
    searchItems: locations,
    searchItemsToTrack: locationsToTrack,
    bounds,
    handleZoomChange,
    handleMoveEnd,
    handleIdle,
    debouncedFetchData: debouncedFetchDayPasses,
  } = useSearchData<SimpleLocationSearchDto, SearchDayPassesLocationsProps>({
    getDatas: searchDayPassesLocations,
  });

  useEffect(() => {
    const fetchPricesAsync = async () => {
      const dayPassesPricesResponse = await fetchDayPassLocationPricingByIds(
        locations.map((location) => location.id)
      );
      setDayPassesPrices(dayPassesPricesResponse);
    };

    if (locations.length > 0) {
      fetchPricesAsync();
    }
  }, [locations]);

  useEffect(() => {
    if (locationsToTrack.length === 0) return;

    gtm.pushECommerceEvent('view_item_list', {
      item_list_id: 'search_page',
      item_list_name: 'Search page',
      items: locationsToTrack.map((x) => gtm.mapCoworkingToItemEcommerce(x)),
    });
  }, [locationsToTrack]);

  useEffect(() => {
    if (bounds === undefined) return;

    debouncedFetchDayPasses({
      locale,
      ...mapFilterToDto(filter),
      within: mapBounds(bounds),
    });
  }, [bounds, filter]);

  const handleLocationDateRangeFilterChange = (
    filters?: LocationDateRangeFilters | undefined
  ) => {
    updateQuery(createDayPassQueryParams(filters));
    if (filters?.localization) {
      const { coords, query } = filters.localization;
      onSearchInputItemClick?.(coords, query, false);
    }
  };

  return (
    <>
      {!isFullScreenMap && (
        <CardListWrapper
          withMap={isWebGLSupported === true || isWebGLSupported === undefined}
          ref={cardListRef}
        >
          {isLoading && (
            <LocationsSearchListLoader
              dataTestId="day-passes-search-list-loader"
              cardHeight={250}
            />
          )}

          {!isLoading && locations.length > 0 && (
            <>
              <div className="flex justify-between mb-5 md:items-center max-md:flex-col">
                <ListCounter className="max-md:mb-2">
                  {t('search-office-space.count', {
                    count: locations.length,
                  })}
                </ListCounter>
                <CoworkingProductExplainer className="self-end" />
              </div>
              <LocationSearchList
                locations={locations}
                prices={dayPassesPrices}
                dateRange={filter.dateRange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                cardHeight={250}
              />
            </>
          )}
          {!isLoading && locations.length === 0 && (
            <SearchNoResults
              hasFilters={hasFilters}
              noResultsWithFiltersMessage={t(
                'coworking-no-results-with-filters'
              )}
            />
          )}
        </CardListWrapper>
      )}
      <LocationSearchMap
        locations={locations}
        mapboxAccessToken={mapboxAccessToken}
        isFullScreenMap={isFullScreenMap}
        isWebGLSupported={isWebGLSupported}
        highlightedSlug={highlightedSlug}
        onMoveEnd={handleMoveEnd}
        onZoomChange={handleZoomChange}
        onIdle={handleIdle}
      />
      <LocationDateRangeModal
        isOpen={searchModalType === SearchModalType.LocationDateRange}
        dateRange={filter.dateRange}
        onClose={onCloseModal}
        onFiltersChange={handleLocationDateRangeFilterChange}
        onOpenChange={() => openModal(SearchModalType.LocationDateRange)}
      />
    </>
  );
}
